import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SupabaseClient } from '../supabase/client';
import {
  createStoreCategory,
  listStoreCategories,
  mapCategoryError,
  reorderStoreCategories,
  softDeleteStoreCategory,
  updateStoreCategory,
} from '../services/category.service';
import type {
  CategoryErrorKey,
  CreateStoreCategoryInput,
  DeleteStoreCategoryInput,
  ReorderStoreCategoriesInput,
  StoreCategoryWithMedia,
  UpdateStoreCategoryInput,
} from '../types/category';

export const categoryKeys = {
  all: ['store-categories'] as const,
  list: (storeId: string) => ['store-categories', 'list', storeId] as const,
};

export interface UseStoreCategoriesResult extends Pick<
  UseQueryResult<StoreCategoryWithMedia[]>,
  'data' | 'isLoading' | 'isError' | 'error' | 'refetch'
> {
  readonly categories: StoreCategoryWithMedia[];
  readonly errorKey: CategoryErrorKey | null;
}

export function useStoreCategories(
  client: SupabaseClient,
  storeId: string | null,
): UseStoreCategoriesResult {
  const q = useQuery({
    queryKey: storeId ? categoryKeys.list(storeId) : categoryKeys.all,
    queryFn: () => listStoreCategories(client, storeId ?? ''),
    enabled: !!storeId,
    staleTime: 60_000,
  });

  return {
    data: q.data,
    categories: q.data ?? [],
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
    errorKey: q.error ? mapCategoryError(q.error) : null,
  };
}

export function useCreateCategory(
  client: SupabaseClient,
): UseMutationResult<StoreCategoryWithMedia, unknown, CreateStoreCategoryInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createStoreCategory(client, input),
    onSuccess: (_category, input) => {
      void queryClient.invalidateQueries({ queryKey: categoryKeys.list(input.store_id) });
      void queryClient.invalidateQueries({ queryKey: ['products', input.store_id] });
    },
  });
}

export function useUpdateCategory(
  client: SupabaseClient,
): UseMutationResult<StoreCategoryWithMedia, unknown, UpdateStoreCategoryInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => updateStoreCategory(client, input),
    onSuccess: (category) => {
      void queryClient.invalidateQueries({ queryKey: categoryKeys.list(category.store_id) });
      void queryClient.invalidateQueries({ queryKey: ['products', category.store_id] });
    },
  });
}

export function useReorderCategories(
  client: SupabaseClient,
): UseMutationResult<void, unknown, ReorderStoreCategoriesInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => reorderStoreCategories(client, input),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: categoryKeys.list(input.store_id) });
    },
  });
}

export function useDeleteCategory(
  client: SupabaseClient,
): UseMutationResult<void, unknown, DeleteStoreCategoryInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => softDeleteStoreCategory(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
